/**
 * @file Donnees PERSISTANTES du joueur en memoire (avec Decimal). Seuls les services du core les modifient ; UI/render les lisent en DeepReadonly.
 */
import type { Decimal } from '@shared/bignum';
import type { BiomeId, GradeId, Locale, ModeId, UpgradeId, WalletId, WeaponBehavior } from '@shared/enums';
import type { Uid } from '@shared/types';
import type { BuffId, GachaPoolId, MetricId, TicketId } from '@config/types';

export interface PlayerMeta {
  /** Identifiant stable du joueur (synchro serveur future). */
  playerId: string;
  deviceId: string;
  nickname: string;
  createdAtMs: number;
  lastSavedAtMs: number;
  /** Dernier instant ou la simulation tournait (base du calcul hors-ligne). */
  lastSeenMs: number;
  /** Graine maitre de la partie ; les flux RNG runtime en derivent. */
  seed: number;
  /** Incremente a chaque sauvegarde (resolution de conflits de synchro future). */
  revision: number;
}

/** Donnees remises a zero par un Rebirth. */
export interface RunData {
  upgrades: Record<UpgradeId, number>;
  /** Argent cumule gagne depuis le dernier Rebirth (base des Ruby). */
  moneyAccum: Decimal;
  /** Niveau courant du mode Main, 0-based : (stage-1)*levelsPerStage + (niveau-1). */
  stageValue: number;
  /** Plus haut niveau atteint dans la run. */
  bestStageValue: number;
  /** 'farm' : niveau rejoue sans chrono (apres echec) ; 'challenge' : niveau chronometre en cours. */
  mainPhase: 'farm' | 'challenge';
  playTimeSeconds: number;
}

export interface ProgressData {
  /** Record absolu (toutes runs) : sert aux deblocages de modes/fonctionnalites. */
  bestStageValue: number;
  modeBests: Partial<Record<ModeId, number>>;
  /** Meilleur palier "balayable" (sweep) par mode. */
  sweepUnlocked: Partial<Record<ModeId, number>>;
  totalKills: number;
}

export interface WeaponItem {
  uid: Uid;
  defId: string;
  locked: boolean;
  /** Ordre d'obtention (tri stable de l'inventaire). */
  obtainedSeq: number;
}

export interface CollectionEntry {
  seen: boolean;
  /** Nombre total obtenu (tirage + fusion). */
  obtained: number;
}

export interface WeaponsData {
  /** Toutes les armes possedees (equipees ou non). */
  items: WeaponItem[];
  /** uid equipe par slot (longueur = BALANCE.equipSlotUnlocks.length). */
  equipped: Array<Uid | null>;
  /** Emplacements payants achetes (0..chest.paidSlots). Capacite du coffre = freeSlots + chestPaidSlots (armes NON equipees). */
  chestPaidSlots: number;
  collection: Record<string, CollectionEntry>;
  /** Cles de recompense de collection deja reclamees (ex. "grade:B"). */
  collectionClaimed: string[];
  pity: Record<GachaPoolId, number>;
  autoMerge: boolean;
  nextSeq: number;
  /** Jour UTC du dernier tirage gratuit quotidien. */
  dailyFreeDrawDay: number;
}

export interface StarsData {
  /** Niveau d'etoiles par slot d'equipement (0..20). */
  levels: number[];
  /** Echecs consecutifs par slot (garantie progressive a partir du niveau 18). */
  pityFails: number[];
}

export interface SkinsData {
  owned: string[];
  equipped: string;
}

export interface AmmoSkinsData {
  owned: string[];
  /** Skin de munitions equipe par categorie d'arme (comportement). */
  equipped: Record<WeaponBehavior, string>;
}

export interface AurasData {
  owned: string[];
  /** Aura portee par le chat chef (null = aucune). */
  equipped: string | null;
  /** Interrupteur FX (bouton du hub) : l'aura equipee n'est dessinee que si vrai. */
  enabled: boolean;
}

export interface FireworksData {
  owned: string[];
  equipped: string;
}

export interface ShotFxData {
  owned: string[];
  /** FX de tir equipe (null = aucun) : se superpose au skin de munition de toutes les armes. */
  equipped: string | null;
}

export interface HubData {
  /** Jetons d'herbe a chat recoltes dans le hub. */
  catnip: number;
}

export interface BuffState {
  /** Secondes restantes (cumul plafonne a maxSeconds). */
  seconds: number;
  superActive: boolean;
  /** Derniere activation gratuite (ms epoch). */
  lastFreeMs: number;
}

export type RebirthStat = 'damage' | 'attackSpeed' | 'money';
export interface RebirthData {
  count: number;
  levels: Record<RebirthStat, number>;
  /** Jour UTC de la derniere utilisation du Rebirth x3. */
  dailyBonusDay: number;
  totalRubyEarned: Decimal;
}

export interface RelicsData {
  levels: Record<string, number>;
}

export interface PetsData {
  owned: Record<string, { level: number; count: number }>;
  equipped: string[];
}

export interface FishData {
  owned: string[];
}

export interface Expedition {
  biome: BiomeId;
  petId: string | null;
  startedMs: number;
  durationSeconds: number;
}
export interface AdventureData {
  expeditions: Expedition[];
  level: number;
  xp: number;
}

export interface TicketState {
  count: number;
  /** Instant de reference de la regeneration (ms epoch). */
  lastRegenMs: number;
}

export interface MissionProgress {
  id: string;
  progress: number;
  target: number;
  claimed: boolean;
}

export interface MissionsData {
  daily: MissionProgress[];
  weekly: MissionProgress[];
  achievements: Record<string, MissionProgress>;
  dayKey: number;
  weekKey: number;
  /** Compteurs cumules a vie (les missions stockent leur propre progression depuis leur tirage). */
  lifetime: Record<MetricId, number>;
}

export interface PassData {
  seasonId: number;
  xp: number;
  epicUnlocked: boolean;
  claimedFree: number[];
  claimedEpic: number[];
}

export interface DailyData {
  /** Jour 1..7 du prochain cadeau. */
  nextDay: number;
  lastClaimDayKey: number;
}

export interface OfflineReport {
  awaySeconds: number;
  /** Secondes effectivement simulees (plafonnees). */
  simulatedSeconds: number;
  capped: boolean;
  money: Decimal;
  kills: number;
  ticketsRegenerated: number;
}

export interface OfflineData {
  /** Rapport en attente de reclamation (affiche par OfflineModal), null sinon. */
  pending: OfflineReport | null;
}

export interface SettingsData {
  locale: Locale;
  /** Reglages joueur : appliques uniquement si le mode courant laisse `autoMove/autoFire` a 'player'. */
  autoMove: boolean;
  autoFire: boolean;
  sfxVolume: number;
  musicVolume: number;
  muted: boolean;
  /** Mode Eco : DPR reduit, moins de particules. */
  eco: boolean;
  quality: 'low' | 'high';
  vibration: boolean;
  damageNumbers: boolean;
  /** Accessibilite : remplace pops/rebonds/confettis par des fondus (en plus de la preference systeme). */
  reduceMotion: boolean;
  /** Achat automatique des upgrades (gratuit, debloque par mission/passe). */
  autoUpgrade: boolean;
  /** Effets cosmetiques affiches (aura, FX de tir, feux d'artifice) : les miens / ceux des autres joueurs du hub. Affichage local seul. */
  fxSelf: boolean;
  fxOthers: boolean;
}

export interface TutorialData {
  done: boolean;
  stepIndex: number;
}

export interface PlayerData {
  meta: PlayerMeta;
  wallet: Record<WalletId, Decimal>;
  run: RunData;
  progress: ProgressData;
  weapons: WeaponsData;
  stars: StarsData;
  skins: SkinsData;
  ammoSkins: AmmoSkinsData;
  auras: AurasData;
  fireworks: FireworksData;
  shotFx: ShotFxData;
  hub: HubData;
  buffs: Record<BuffId, BuffState>;
  rebirth: RebirthData;
  relics: RelicsData;
  pets: PetsData;
  fish: FishData;
  adventure: AdventureData;
  tickets: Record<TicketId, TicketState>;
  missions: MissionsData;
  pass: PassData;
  daily: DailyData;
  offline: OfflineData;
  settings: SettingsData;
  tutorial: TutorialData;
  /** Drapeaux libres (fonctionnalites activees, ex. "hubEnabled"). */
  flags: Record<string, boolean>;
}

/** Chemins des champs Decimal (wildcard `*` = toutes les cles d'un Record). Source de verite de la (de)serialisation. */
export const DECIMAL_PATHS: readonly string[] = [
  'wallet.*',
  'run.moneyAccum',
  'rebirth.totalRubyEarned',
  'offline.pending.money',
];

/** Grade minimal de deblocage de chaque arme dans la collection (utilitaire de typage). */
export type CollectionKey = `grade:${GradeId}`;
